import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { fetchAppConfig } from '../../utils/appConfig'
import {
  findNewVersion,
  hideVersion,
  isVersionHidden,
  reloadApp,
  wasReloadAttempted,
} from '../../utils/appVersion'
import MaintenanceView, { OVERLAY_ATTRIBUTE } from './MaintenanceView'
import NewVersionBar from './NewVersionBar'

const REFRESH_INTERVAL = 60000

const AppConfigWrapper = ({ children }: PropsWithChildren<{}>) => {
  const [maintenance, setMaintenance] = useState(false)
  const [maintenanceMsg, setMaintenanceMsg] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [checkedAt, setCheckedAt] = useState<Date | null>(null)
  const [newVersionTag, setNewVersionTag] = useState('')
  const [versionHidden, setVersionHidden] = useState(false)
  const [versionStuck, setVersionStuck] = useState(false)
  const requestId = useRef(0)

  const getConfig = useCallback(async () => {
    const id = ++requestId.current

    const config = fetchAppConfig()
    findNewVersion()
      .then(tag => {
        if (tag && id === requestId.current) {
          setNewVersionTag(tag)
          // re-read on every cycle, so a reminder the user put off comes back
          // on its own once the snooze has run out
          setVersionHidden(isVersionHidden(tag))
          setVersionStuck(wasReloadAttempted(tag))
        }
      })
      .catch(error => console.log('[App version error]:', error))

    try {
      const data = await config
      if (id !== requestId.current) {
        return
      }
      setMaintenance(!!data.maintenance_mycompany)
      setMaintenanceMsg(
        typeof data.maintenance_mycompany_msg === 'string'
          ? data.maintenance_mycompany_msg
          : '',
      )
    } catch (error) {
      console.log('[App config error]:', error)
      if (id !== requestId.current) {
        return
      }
    }

    setCheckedAt(new Date())
  }, [])

  const hideNewVersion = (forGood: boolean) => {
    hideVersion(newVersionTag, forGood)
    setVersionHidden(true)
  }

  const refreshConfig = async () => {
    setRefreshing(true)
    await getConfig()
    setRefreshing(false)
  }

  useEffect(() => {
    if (!maintenance) {
      return
    }

    const blocked = Array.from(document.body.children).filter(
      element =>
        !element.hasAttribute(OVERLAY_ATTRIBUTE) &&
        !element.hasAttribute('inert'),
    )
    blocked.forEach(element => element.setAttribute('inert', ''))

    return () => blocked.forEach(element => element.removeAttribute('inert'))
  }, [maintenance])

  useEffect(() => {
    getConfig()

    const interval = setInterval(getConfig, REFRESH_INTERVAL)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        getConfig()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [getConfig])

  return (
    <>
      {children}
      {!maintenance &&
        !!newVersionTag &&
        !versionHidden &&
        createPortal(
          <NewVersionBar
            stuck={versionStuck}
            onReload={() => reloadApp(newVersionTag)}
            onSnooze={() => hideNewVersion(false)}
            onClose={() => hideNewVersion(true)}
          />,
          document.body,
        )}
      {maintenance &&
        createPortal(
          <MaintenanceView
            message={maintenanceMsg}
            onRefresh={refreshConfig}
            refreshing={refreshing}
            checkedAt={checkedAt}
          />,
          document.body,
        )}
    </>
  )
}

export default AppConfigWrapper
